import React, { useState, useEffect } from 'react';
import { useQuery } from 'react-query';
import { todaysOrdersAPI } from '../services/api';
import toast from 'react-hot-toast';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell, PieChart, Pie } from 'recharts';
import { Calendar, Package, Droplet, TrendingUp, Download } from 'lucide-react';
import LoadingSpinner from '../components/LoadingSpinner';

const OrderAnalytics = () => {
  const [dateRange, setDateRange] = useState({
    startDate: new Date(Date.now() - 6 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });

  const [viewType, setViewType] = useState('daily'); // daily, weekly, summary

  // Fetch analytics data
  const { data: analyticsData, isLoading, error, refetch } = useQuery(
    ['orderAnalytics', dateRange],
    () => todaysOrdersAPI.getAnalytics({
      start_date: dateRange.startDate,
      end_date: dateRange.endDate
    }),
    {
      retry: 2,
      onError: (error) => {
        console.error('Analytics error:', error);
        toast.error('Failed to load order analytics');
      }
    }
  );

  const analytics = analyticsData?.data?.data || {
    summary: { totalOrders: 0, totalItems: 0, totalQuantity: 0 },
    dateWiseBreakdown: [],
    productsByUnit: [],
    bottleSizeSummary: []
  };

  const handleDateChange = (field, value) => {
    setDateRange(prev => ({ ...prev, [field]: value }));
  };

  const handleExportCSV = () => {
    if (!analytics.productsByUnit || analytics.productsByUnit.length === 0) {
      toast.error('No data to export');
      return;
    }

    let csvContent = 'Product,Unit/Size,Total Quantity,Category\n';
    
    analytics.productsByUnit.forEach(item => {
      csvContent += `"${item.productName}","${item.unit}",${item.totalQuantity},"${item.category}"\n`;
    });

    const element = document.createElement('a');
    element.setAttribute('href', 'data:text/csv;charset=utf-8,' + encodeURIComponent(csvContent));
    element.setAttribute('download', `order-analytics-${dateRange.startDate}-to-${dateRange.endDate}.csv`);
    element.style.display = 'none';
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
    
    toast.success('Data exported successfully');
  };

  const COLORS = ['#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'];

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="card-gradient p-6 rounded-xl">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold gradient-text mb-2">Order Analytics</h1>
            <p className="text-gray-600 dark:text-gray-300">
              Track orders, items, and bottle requirements
            </p>
          </div>
          <button
            onClick={handleExportCSV}
            className="btn-primary flex items-center gap-2 px-4 py-2 rounded-lg"
          >
            <Download className="h-4 w-4" />
            Export CSV
          </button>
        </div>
      </div>

      {/* Date Range Filter */}
      <div className="card-modern">
        <div className="flex flex-col sm:flex-row gap-4 items-end">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Start Date
            </label>
            <input
              type="date"
              value={dateRange.startDate}
              onChange={(e) => handleDateChange('startDate', e.target.value)}
              className="input-modern w-full"
            />
          </div>
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              End Date
            </label>
            <input
              type="date"
              value={dateRange.endDate}
              onChange={(e) => handleDateChange('endDate', e.target.value)}
              className="input-modern w-full"
            />
          </div>
          <button
            onClick={() => refetch()}
            className="btn-primary px-6 py-2 rounded-lg"
          >
            Load Data
          </button>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="card-modern p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 dark:text-gray-400 text-sm font-medium">Total Orders</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-white mt-2">
                {analytics.summary?.totalOrders || 0}
              </p>
            </div>
            <Package className="h-12 w-12 text-blue-500 opacity-20" />
          </div>
        </div>

        <div className="card-modern p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 dark:text-gray-400 text-sm font-medium">Total Items</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-white mt-2">
                {analytics.summary?.totalItems || 0}
              </p>
            </div>
            <Droplet className="h-12 w-12 text-orange-500 opacity-20" />
          </div>
        </div>

        <div className="card-modern p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 dark:text-gray-400 text-sm font-medium">Total Quantity</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-white mt-2">
                {analytics.summary?.totalQuantity || 0}
              </p>
            </div>
            <TrendingUp className="h-12 w-12 text-green-500 opacity-20" />
          </div>
        </div>

        <div className="card-modern p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 dark:text-gray-400 text-sm font-medium">Avg Items/Order</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-white mt-2">
                {analytics.summary?.averageItemsPerOrder || '0.00'}
              </p>
            </div>
            <div className="h-12 w-12 bg-purple-100 dark:bg-purple-900 rounded-lg opacity-20" />
          </div>
        </div>
      </div>

      {/* Bottle Size Summary */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card-modern p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Bottles Needed by Product
          </h3>
          {analytics.productsByUnit && analytics.productsByUnit.length > 0 ? (
            <div className="space-y-3">
              {analytics.productsByUnit.map((item, index) => {
                const displayName = item.productName?.trim() || 'Unknown Product';
                return (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: COLORS[index % COLORS.length] }}
                      />
                      <span className="font-medium text-gray-900 dark:text-white">
                        {displayName} ({item.unit || 'Unit'})
                      </span>
                    </div>
                    <span className="text-lg font-bold text-gray-900 dark:text-white">
                      {item.totalQuantity}
                    </span>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-gray-500 dark:text-gray-400">No data available</p>
          )}
        </div>

        {/* Product Distribution Chart */}
        {analytics.productsByUnit && analytics.productsByUnit.length > 0 && (
          <div className="card-modern p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Product Distribution
            </h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={analytics.productsByUnit.map(item => ({
                    ...item,
                    displayName: `${(item.productName?.trim() || 'Unknown Product')} (${item.unit || 'Unit'})`
                  }))}
                  dataKey="totalQuantity"
                  nameKey="displayName"
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  label
                >
                  {analytics.productsByUnit.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => `${value} units`} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Daily Trend Chart */}
      {analytics.dateWiseBreakdown && analytics.dateWiseBreakdown.length > 0 && (
        <div className="card-modern p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Daily Order Trend
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={analytics.dateWiseBreakdown}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 12 }}
                angle={-45}
                textAnchor="end"
                height={80}
              />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line
                type="monotone"
                dataKey="ordersCount"
                stroke="#3b82f6"
                name="Orders"
                strokeWidth={2}
              />
              <Line
                type="monotone"
                dataKey="totalItems"
                stroke="#10b981"
                name="Items"
                strokeWidth={2}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Date-wise Item Breakdown Table */}
      <div className="card-modern p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Date-wise Item Breakdown
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700">
                <th className="px-4 py-3 text-left text-gray-900 dark:text-white font-semibold">
                  Date
                </th>
                <th className="px-4 py-3 text-left text-gray-900 dark:text-white font-semibold">
                  Product (Size)
                </th>
                <th className="px-4 py-3 text-left text-gray-900 dark:text-white font-semibold">
                  Category
                </th>
                <th className="px-4 py-3 text-right text-gray-900 dark:text-white font-semibold">
                  Quantity
                </th>
              </tr>
            </thead>
            <tbody>
              {analytics.dateWiseBreakdown && analytics.dateWiseBreakdown.map((dayData) => (
                dayData.items && dayData.items.map((item, itemIndex) => (
                  <tr
                    key={`${dayData.date}-${itemIndex}`}
                    className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50"
                  >
                    <td className="px-4 py-3 text-gray-900 dark:text-white">
                      {dayData.date}
                    </td>
                    <td className="px-4 py-3 text-gray-900 dark:text-white font-medium">
                      {item.productName} ({item.unit})
                    </td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-300">
                      {item.category}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className="inline-block bg-blue-100 dark:bg-blue-900 text-blue-900 dark:text-blue-100 px-3 py-1 rounded-full font-semibold">
                        {item.quantity}
                      </span>
                    </td>
                  </tr>
                ))
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Product Summary Table */}
      <div className="card-modern p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Product Summary (All Sizes)
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700">
                <th className="px-4 py-3 text-left text-gray-900 dark:text-white font-semibold">
                  Product
                </th>
                <th className="px-4 py-3 text-left text-gray-900 dark:text-white font-semibold">
                  Size/Unit
                </th>
                <th className="px-4 py-3 text-left text-gray-900 dark:text-white font-semibold">
                  Category
                </th>
                <th className="px-4 py-3 text-right text-gray-900 dark:text-white font-semibold">
                  Total Quantity
                </th>
              </tr>
            </thead>
            <tbody>
              {analytics.productsByUnit && analytics.productsByUnit.map((product, index) => (
                <tr
                  key={index}
                  className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50"
                >
                  <td className="px-4 py-3 text-gray-900 dark:text-white font-medium">
                    {product.productName}
                  </td>
                  <td className="px-4 py-3 text-gray-900 dark:text-white">
                    {product.unit}
                  </td>
                  <td className="px-4 py-3 text-gray-600 dark:text-gray-300">
                    {product.category}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className="inline-block bg-green-100 dark:bg-green-900 text-green-900 dark:text-green-100 px-3 py-1 rounded-full font-semibold">
                      {product.totalQuantity}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default OrderAnalytics;
